/** @format */

import { apiSlice } from "../apiSlice";
import { Text } from "../../features/resources/resourcesSlice";
import { IsoDateString, ResourceReference } from "../../types";
import { store } from "../store";

export interface Subject {
  id: string;
  title: Text;
  indexed?: boolean;
  created?: IsoDateString;
  modified?: IsoDateString;
}

export interface SubjectResponse {
  subjects: Subject[];
}

export interface ResourcesFromSubjectResponse {
  resourceRefs: ResourceReference[];
  messages: any[];
}

/** API Endpoints related to Subject(s) */
export const subjectApiSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    /**
     * getSubjects query (GET)
     * @returns {SubjectResponse} all available subjects.
     */
    getSubjects: build.query<SubjectResponse, void>({
      query: () => `subjects/get`,
    }),

    /**
     * addSubject mutation (POST)
     * @param subject the new subject value
     * @returns {SubjectResponse} all subjects including newly added.
     */
    addSubject: build.mutation<SubjectResponse, Subject>({
      query: (data) => {
        return {
          url: `subjects/add-subject`,
          method: "POST",
          body: { subject: data },
        };
      },
      transformResponse: (response: { subjects: SubjectResponse }) => response.subjects
      // TODO: ADD SUBJECT TAG
    }),

    /**
     * deleteSubject mutation (DELETE)
     * @param id the id of the subject to delete
     * @returns {SubjectResponse} all subjects with removed subject.
     */
    deleteSubject: build.mutation<SubjectResponse, string>({
      query: (id) => {
        return {
          url: `subjects/delete-subject/${id}`,
          method: "DELETE",
        };
      },
      transformResponse: (response: { subjects: SubjectResponse }) => response.subjects
      // TODO: ADD SUBJECT TAG
    }),

    
    /**
     * updateSubjectIndexes mutation (POST)
     * @param subject[] the id of the subject and indexed values of multiple subjects
     * @returns {SubjectResponse} all subjects with updated indexed values.
     */
    updateSubjectIndexes: build.mutation<SubjectResponse, {subjects: { subjectId: string, indexed: boolean}[]}>({
      query: (data) => {
        return {
          url: `subjects/update-subjects-index`,
          method: "POST",
          body: data
        };
      },
      transformResponse: (response: { subjects: SubjectResponse }) => response.subjects
      // TODO: ADD SUBJECT TAG
    }),

    /**
     * getSubjectsSingleLang query (GET)
     * @param {string} locale - Locale of subject either `en` or `cy`
     * @returns {Subject[]} all available subjects in a single language.
     */
    getSubjectsSingleLang: build.query<Subject[], string>({
      query: (locale) => `subjects/get/${locale}`,
    }),

    /**
     * getResourcesFromSubject query (GET)
     * @param {string} code - The subject's id
     * @param {string} locale - Locale of subject either `en` or `cy`
     * @returns {ResourceReference[]} all available resources in a subject.
     */
    getResourcesFromSubject: build.query<
      ResourceReference[],
      { code: string; locale: string }
    >({
      query: ({ code, locale }) => {
        // If logged in, send request to auth endpoint
        if (store.getState().auth.userToken?.token) {
          return `subjects/resources/auth/${code}/${locale}`;
        }
        return `subjects/resources/${code}/${locale}`;
      },
      transformResponse: (response: { resourceRefs: ResourceReference[] }) =>
        response.resourceRefs,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ UUID }) => ({
                type: "ResourceRef" as const,
                id: UUID,
              })),
              { type: "ResourceRef", id: "LIST" },
            ]
          : [{ type: "ResourceRef", id: "LIST" }],
    }),
  }),
});

export const {
  useGetSubjectsSingleLangQuery,
  useGetSubjectsQuery,
  useGetResourcesFromSubjectQuery,
  useAddSubjectMutation,
  useDeleteSubjectMutation,
  useUpdateSubjectIndexesMutation,
} = subjectApiSlice;
